//  src/hooks/useDynamicLoadingText.js
import { useEffect, useState } from "react";
import { onSnapshot, doc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";

/**
 * Returns a reactive loading-screen headline.
 *   • personalised for signed-in, non-anonymous users
 *   • otherwise falls back to Firestore’s default string
 */
const TOKEN_RE = /\{\{\s*name\s*\}\}|\{name\}|%name%/gi;
const clean = (s) => String(s || "").replace(/\s+/g, " ").trim();
const stripTokens = (tpl) => clean(String(tpl || "").replace(TOKEN_RE, ""));
const applyTemplate = (tpl, user) => {
  if (!tpl) return "";
  const name = user && !user.isAnonymous ? (user.displayName || user.email || "") : "";
  const replaced = String(tpl).replace(TOKEN_RE, name);
  return clean(replaced) || stripTokens(tpl);
};

export default function useDynamicLoadingText() {
  const STORAGE_KEY = "vkzLoadingText";
  const [text, setText] = useState(() => {
    if (typeof window === "undefined") return null;
    const cached = window.localStorage.getItem(STORAGE_KEY) || "";
    if (!cached) return null;
    return applyTemplate(cached, auth.currentUser) || stripTokens(cached) || null;
  });

  useEffect(() => {
    /* ---- keep latest fallback string in this closure ---- */
    let fallback = "";
    if (typeof window !== "undefined") {
      fallback = window.localStorage.getItem(STORAGE_KEY) || "";
    }

    /* helper to combine user + fallback into final text */
    const update = (user) => {
      if (fallback) {
        setText(applyTemplate(fallback, user) || fallback);
        return;
      }
      if (user && !user.isAnonymous) {
        setText(`Lets Get It ${user.displayName || user.email} !!!`);
        return;
      }
      setText(null);
    };

    /* 1⃣  listen to Firestore for default loading text */
    const stopFS = onSnapshot(
      doc(db, "appSettings", "loadingScreen"),
      (snap) => {
        fallback = snap.exists() ? snap.data().loadingText || "" : "";
        if (fallback && typeof window !== "undefined") {
          window.localStorage.setItem(STORAGE_KEY, fallback);
        }
        update(auth.currentUser);            // re-evaluate with current user
      },
      () => update(auth.currentUser)
    );

    /* 2⃣  listen to Firebase Auth state */
    const stopAuth = onAuthStateChanged(auth, (user) => update(user));
    update(auth.currentUser);

    return () => {                // clean-up
      stopFS();
      stopAuth();
    };
  }, []);

  return text;
}
